import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Plus, Edit, Trash2, Play, Eye, RefreshCw, CheckCircle, XCircle, AlertCircle, Clock, Loader2, Copy, Search, ChevronDown, ChevronRight, FileText, Code } from 'lucide-react';

interface InputOutputSpec {
  name: string;
  type: 'text' | 'number' | 'json' | 'boolean';
  description: string;
  required: boolean;
  example: string;
}

interface Pipeline {
  id: string;
  name: string;
  pipeline_id: string;
  environment: 'test' | 'production';
  is_active: boolean;
  required_outputs: string[];
  optional_outputs: string[];
  description: string | null;
  input_specs: InputOutputSpec[];
  output_specs: InputOutputSpec[];
  created_at: string;
  updated_at: string;
}

interface PipelineRun {
  id: string;
  pipeline_id: string | null;
  user_id: string;
  status: 'pending' | 'running' | 'success' | 'partial' | 'failed';
  request_payload: any;
  response_payload: any;
  vectorshift_request: any;
  vectorshift_raw_response_text: string | null;
  vectorshift_raw_response_json: any;
  vectorshift_response_status: number | null;
  vectorshift_response_headers: any;
  missing_required_fields: string[];
  missing_optional_fields: string[];
  error_message: string | null;
  duration_ms: number | null;
  is_smoke_test: boolean;
  created_at: string;
}

const DEFAULT_INPUT_SPECS: InputOutputSpec[] = [
  {
    name: 'Job_Description',
    type: 'text',
    description: 'Full job posting text including title, company, responsibilities, requirements',
    required: true,
    example: 'Company: Acme Corp\nJob Title: Software Engineer\nResponsibilities:\n- Build scalable applications\n- Code review\n\nRequirements:\n- 5+ years experience\n- Python, JavaScript'
  },
  {
    name: 'Resume',
    type: 'text',
    description: 'Full resume text content',
    required: true,
    example: 'John Smith\njohn@email.com\n\nSenior Software Engineer with 10+ years experience in Python and JavaScript...'
  }
];

const DEFAULT_OUTPUT_SPECS: InputOutputSpec[] = [
  {
    name: 'Final_Resume_Text',
    type: 'text',
    required: true,
    description: 'The tailored resume text optimized for the job',
    example: 'John Smith\njohn@email.com\n\nSenior Software Engineer with 10+ years...'
  },
  {
    name: 'Original_Match_Score',
    type: 'number',
    required: true,
    description: 'Match score 0-100 for original resume vs job',
    example: '65'
  },
  {
    name: 'Tailored_Match_Score',
    type: 'number',
    required: true,
    description: 'Match score 0-100 for tailored resume vs job',
    example: '88'
  }
];

const AdminPipelinesManagement: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [runs, setRuns] = useState<PipelineRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [selectedRun, setSelectedRun] = useState<PipelineRun | null>(null);
  const [showRunDetail, setShowRunDetail] = useState(false);
  const [showPipelineForm, setShowPipelineForm] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null);
  const [runningTest, setRunningTest] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSpecs, setExpandedSpecs] = useState<Record<string, boolean>>({});
  const [runDetailTab, setRunDetailTab] = useState('summary');
  const [rawViewMode, setRawViewMode] = useState(false);
  const [stoppingRunId, setStoppingRunId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    pipeline_id: '',
    environment: 'test' as 'test' | 'production',
    is_active: false,
    required_outputs: 'Final_Resume_Text, Original_Match_Score, Tailored_Match_Score',
    optional_outputs: 'Cover_Letter, Thank_You_Letter, Executive_Bio, Linkedin_Profile, Outreach_Letter',
    description: '',
    input_specs: DEFAULT_INPUT_SPECS,
    output_specs: DEFAULT_OUTPUT_SPECS
  });

  useEffect(() => {
    loadPipelines();
    loadRuns();
  }, []);

  const handleStopRun = async (runId: string) => {
    setStoppingRunId(runId);
    try {
      const { error } = await supabase
        .from('vectorshift_runs')
        .update({
          status: 'failed',
          error_message: 'Manually stopped by admin'
        })
        .eq('id', runId);
      
      if (error) throw error;
      
      toast({
        title: 'Run stopped',
        description: `Run ${runId.substring(0, 8)} has been marked as failed.`
      });
      
      loadRuns();
    } catch (error: any) {
      toast({
        title: 'Error stopping run',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setStoppingRunId(null);
    }
  };

  const loadPipelines = async () => {
    try {
      const { data, error } = await supabase
        .from('vectorshift_pipelines')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const parsed: Pipeline[] = (data || []).map(p => {
        let reqOutputs: string[] = [];
        let optOutputs: string[] = [];
        let inputSpecs: InputOutputSpec[] = [];
        let outputSpecs: InputOutputSpec[] = [];
        
        if (Array.isArray(p.required_outputs)) {
          reqOutputs = p.required_outputs as string[];
        } else if (typeof p.required_outputs === 'string') {
          try { reqOutputs = JSON.parse(p.required_outputs); } catch { reqOutputs = []; }
        }
        
        if (Array.isArray(p.optional_outputs)) {
          optOutputs = p.optional_outputs as string[];
        } else if (typeof p.optional_outputs === 'string') {
          try { optOutputs = JSON.parse(p.optional_outputs); } catch { optOutputs = []; }
        }

        if (Array.isArray(p.input_specs)) {
          inputSpecs = p.input_specs as unknown as InputOutputSpec[];
        }
        
        if (Array.isArray(p.output_specs)) {
          outputSpecs = p.output_specs as unknown as InputOutputSpec[];
        }
        
        return {
          id: p.id,
          name: p.name,
          pipeline_id: p.pipeline_id,
          environment: p.environment as 'test' | 'production',
          is_active: p.is_active,
          required_outputs: reqOutputs,
          optional_outputs: optOutputs,
          input_specs: inputSpecs,
          output_specs: outputSpecs,
          description: p.description,
          created_at: p.created_at,
          updated_at: p.updated_at
        };
      });
      
      setPipelines(parsed);
    } catch (error: any) {
      console.error('Error loading pipelines:', error);
      toast({
        title: 'Error loading pipelines',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRuns = async () => {
    setLoadingRuns(true);
    try {
      const { data, error } = await supabase
        .from('vectorshift_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      
      const typedRuns: PipelineRun[] = (data || []).map(r => ({
        ...r,
        status: r.status as 'pending' | 'running' | 'success' | 'partial' | 'failed',
        vectorshift_request: r.vectorshift_request || null
      }));
      
      setRuns(typedRuns);
    } catch (error: any) {
      console.error('Error loading runs:', error);
      toast({
        title: 'Error loading runs',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoadingRuns(false);
    }
  };

  const handleSavePipeline = async () => {
    try {
      // Derive required/optional outputs from output_specs (the checkbox is the source of truth)
      const requiredOutputs = formData.output_specs
        .filter(spec => spec.required && spec.name.trim())
        .map(spec => spec.name.trim());
      
      const optionalOutputs = formData.output_specs
        .filter(spec => !spec.required && spec.name.trim())
        .map(spec => spec.name.trim());

      const pipelineData = {
        name: formData.name,
        pipeline_id: formData.pipeline_id,
        environment: formData.environment,
        is_active: formData.is_active,
        required_outputs: requiredOutputs,
        optional_outputs: optionalOutputs,
        description: formData.description || null,
        input_specs: JSON.parse(JSON.stringify(formData.input_specs)),
        output_specs: JSON.parse(JSON.stringify(formData.output_specs))
      };

      if (editingPipeline) {
        const { error } = await supabase
          .from('vectorshift_pipelines')
          .update(pipelineData)
          .eq('id', editingPipeline.id);

        if (error) throw error;
        toast({ title: 'Pipeline updated successfully' });
      } else {
        const { error } = await supabase
          .from('vectorshift_pipelines')
          .insert(pipelineData);

        if (error) throw error;
        toast({ title: 'Pipeline created successfully' });
      }

      setShowPipelineForm(false);
      setEditingPipeline(null);
      resetForm();
      loadPipelines();
    } catch (error: any) {
      console.error('Error saving pipeline:', error);
      toast({
        title: 'Error saving pipeline',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleDeletePipeline = async (id: string) => {
    if (!confirm('Are you sure you want to delete this pipeline?')) return;

    try {
      const { error } = await supabase
        .from('vectorshift_pipelines')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: 'Pipeline deleted successfully' });
      loadPipelines();
    } catch (error: any) {
      console.error('Error deleting pipeline:', error);
      toast({
        title: 'Error deleting pipeline',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleEditPipeline = (pipeline: Pipeline) => {
    setEditingPipeline(pipeline);
    setFormData({
      name: pipeline.name,
      pipeline_id: pipeline.pipeline_id,
      environment: pipeline.environment,
      is_active: pipeline.is_active,
      required_outputs: pipeline.required_outputs.join(', '),
      optional_outputs: pipeline.optional_outputs.join(', '),
      description: pipeline.description || '',
      input_specs: pipeline.input_specs.length > 0 ? pipeline.input_specs : DEFAULT_INPUT_SPECS,
      output_specs: pipeline.output_specs.length > 0 ? pipeline.output_specs : DEFAULT_OUTPUT_SPECS
    });
    setShowPipelineForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      pipeline_id: '',
      environment: 'test',
      is_active: false,
      required_outputs: 'Final_Resume_Text, Original_Match_Score, Tailored_Match_Score',
      optional_outputs: 'Cover_Letter, Thank_You_Letter, Executive_Bio, Linkedin_Profile, Outreach_Letter',
      description: '',
      input_specs: DEFAULT_INPUT_SPECS,
      output_specs: DEFAULT_OUTPUT_SPECS
    });
  };

  const handleRunSmokeTest = async (pipeline: Pipeline) => {
    setRunningTest(true);
    try {
      toast({
        title: 'Smoke test started',
        description: `Testing pipeline: ${pipeline.name}`
      });
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: 'Smoke test completed',
        description: 'Check the runs tab for results'
      });
      
      loadRuns();
    } catch (error: any) {
      toast({
        title: 'Smoke test failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setRunningTest(false);
    }
  };

  const addInputSpec = () => {
    setFormData({
      ...formData,
      input_specs: [...formData.input_specs, { name: '', type: 'text', description: '', required: true, example: '' }]
    });
  };

  const removeInputSpec = (index: number) => {
    setFormData({
      ...formData,
      input_specs: formData.input_specs.filter((_, i) => i !== index)
    });
  };

  const updateInputSpec = (index: number, field: keyof InputOutputSpec, value: any) => {
    const updated = [...formData.input_specs];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, input_specs: updated });
  };

  const addOutputSpec = () => {
    setFormData({
      ...formData,
      output_specs: [...formData.output_specs, { name: '', type: 'text', description: '', required: false, example: '' }]
    });
  };

  const removeOutputSpec = (index: number) => {
    setFormData({
      ...formData,
      output_specs: formData.output_specs.filter((_, i) => i !== index)
    });
  };

  const updateOutputSpec = (index: number, field: keyof InputOutputSpec, value: any) => {
    const updated = [...formData.output_specs];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, output_specs: updated });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'partial':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-500">Success</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-500">Partial</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'running':
        return <Badge className="bg-blue-500">Running</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  const filteredRuns = runs.filter(run => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      run.id.toLowerCase().includes(query) ||
      run.status.toLowerCase().includes(query) ||
      (run.error_message && run.error_message.toLowerCase().includes(query))
    );
  });

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: `${label} copied to clipboard` });
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  const toggleSpecs = (pipelineId: string) => {
    setExpandedSpecs(prev => ({ ...prev, [pipelineId]: !prev[pipelineId] }));
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'text': return 'bg-blue-100 text-blue-700';
      case 'number': return 'bg-green-100 text-green-700';
      case 'json': return 'bg-purple-100 text-purple-700';
      case 'boolean': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const renderFullTextArea = (label: string, value: string | null | undefined, onCopy: () => void) => {
    const displayValue = value || '(empty)';
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">{label}</h4>
          <div className="flex gap-2">
            <Badge variant="secondary">{typeof value === 'string' ? value.length : 0} chars</Badge>
            <Button variant="ghost" size="sm" onClick={onCopy}>
              <Copy className="w-4 h-4 mr-1" />
              Copy
            </Button>
          </div>
        </div>
        <div className="relative">
          <pre className="p-4 bg-muted rounded-lg text-xs overflow-auto font-mono whitespace-pre-wrap break-all border"
               style={{ maxHeight: 'none', minHeight: '100px' }}>
            {displayValue}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Pipeline Management</h1>
              <p className="text-muted-foreground">Manage VectorShift pipelines with full input/output specifications</p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setEditingPipeline(null); setShowPipelineForm(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Pipeline
          </Button>
        </div>

        <Tabs defaultValue="pipelines" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pipelines">Pipelines ({pipelines.length})</TabsTrigger>
            <TabsTrigger value="runs">Run History ({runs.length})</TabsTrigger>
          </TabsList>

          {/* Pipelines Tab */}
          <TabsContent value="pipelines">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : pipelines.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground mb-4">No pipelines configured yet</p>
                  <Button onClick={() => { resetForm(); setShowPipelineForm(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Pipeline
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pipelines.map((pipeline) => (
                  <Card key={pipeline.id} className={pipeline.is_active ? 'border-green-500/50' : ''}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-xl">{pipeline.name}</CardTitle>
                            <Badge variant={pipeline.environment === 'production' ? 'default' : 'secondary'}>
                              {pipeline.environment}
                            </Badge>
                            {pipeline.is_active && <Badge className="bg-green-500">Active</Badge>}
                          </div>
                          <CardDescription className="font-mono text-sm">
                            Pipeline ID: {pipeline.pipeline_id}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleRunSmokeTest(pipeline)} disabled={runningTest}>
                            {runningTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            <span className="ml-2">Test</span>
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleEditPipeline(pipeline)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDeletePipeline(pipeline.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {pipeline.description && (
                          <p className="text-sm text-muted-foreground">{pipeline.description}</p>
                        )}
                        
                        {/* Collapsible Specifications */}
                        <Collapsible open={expandedSpecs[pipeline.id]} onOpenChange={() => toggleSpecs(pipeline.id)}>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" className="w-full justify-between p-2 h-auto">
                              <span className="flex items-center gap-2 text-sm font-medium">
                                <FileText className="w-4 h-4" />
                                View Input/Output Specifications
                              </span>
                              {expandedSpecs[pipeline.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="pt-4 space-y-4">
                            {/* Input Specifications */}
                            <div className="border rounded-lg p-4">
                              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                <Code className="w-4 h-4 text-blue-500" />
                                INPUTS EXPECTED
                              </h4>
                              {pipeline.input_specs.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Required</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead>Example</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {pipeline.input_specs.map((spec, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell className="font-mono text-sm font-medium">{spec.name}</TableCell>
                                          <TableCell>
                                            <Badge className={getTypeColor(spec.type)}>{spec.type}</Badge>
                                          </TableCell>
                                          <TableCell>
                                            {spec.required ? (
                                              <Badge variant="destructive">Yes</Badge>
                                            ) : (
                                              <Badge variant="secondary">No</Badge>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-sm max-w-xs truncate">{spec.description}</TableCell>
                                          <TableCell>
                                            <Dialog>
                                              <Button variant="outline" size="sm" asChild>
                                                <span className="cursor-pointer">View Example</span>
                                              </Button>
                                            </Dialog>
                                            <details className="inline-block ml-2">
                                              <summary className="cursor-pointer text-xs text-blue-600">Show</summary>
                                              <pre className="mt-2 p-2 bg-muted rounded text-xs max-w-md overflow-auto whitespace-pre-wrap">
                                                {spec.example || '(no example)'}
                                              </pre>
                                            </details>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">No input specifications defined</p>
                              )}
                            </div>

                            {/* Output Specifications */}
                            <div className="border rounded-lg p-4">
                              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                <Code className="w-4 h-4 text-green-500" />
                                OUTPUTS EXPECTED
                              </h4>
                              {pipeline.output_specs.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Required</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead>Example</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {pipeline.output_specs.map((spec, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell className="font-mono text-sm font-medium">{spec.name}</TableCell>
                                          <TableCell>
                                            <Badge className={getTypeColor(spec.type)}>{spec.type}</Badge>
                                          </TableCell>
                                          <TableCell>
                                            {spec.required ? (
                                              <Badge variant="destructive">Yes</Badge>
                                            ) : (
                                              <Badge variant="secondary">No</Badge>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-sm max-w-xs truncate">{spec.description}</TableCell>
                                          <TableCell>
                                            <details className="inline-block">
                                              <summary className="cursor-pointer text-xs text-blue-600">Show</summary>
                                              <pre className="mt-2 p-2 bg-muted rounded text-xs max-w-md overflow-auto whitespace-pre-wrap">
                                                {spec.example || '(no example)'}
                                              </pre>
                                            </details>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">No output specifications defined</p>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>

                        {/* Legacy Required/Optional Outputs */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-medium mb-2">Required Outputs</h4>
                            <div className="flex flex-wrap gap-1">
                              {pipeline.required_outputs.map((output) => (
                                <Badge key={output} variant="outline" className="border-red-300 text-red-700">
                                  {output}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium mb-2">Optional Outputs</h4>
                            <div className="flex flex-wrap gap-1">
                              {pipeline.optional_outputs.map((output) => (
                                <Badge key={output} variant="outline">
                                  {output}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Runs Tab */}
          <TabsContent value="runs">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Run History</CardTitle>
                    <CardDescription>View all pipeline execution logs with FULL payloads</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search runs..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-64"
                      />
                    </div>
                    <Button variant="outline" size="icon" onClick={loadRuns} disabled={loadingRuns}>
                      <RefreshCw className={`w-4 h-4 ${loadingRuns ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingRuns ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredRuns.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No runs found
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Run ID</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Raw Data</TableHead>
                        <TableHead>Missing Fields</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRuns.map((run) => (
                        <TableRow key={run.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(run.status)}
                              {getStatusBadge(run.status)}
                              {run.is_smoke_test && <Badge variant="outline">Test</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {run.id.substring(0, 8)}...
                          </TableCell>
                          <TableCell>
                            {run.duration_ms ? `${(run.duration_ms / 1000).toFixed(2)}s` : '-'}
                          </TableCell>
                          <TableCell>
                            {run.error_message === 'Manually stopped by admin' ? (
                              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                                Stopped
                              </Badge>
                            ) : run.status === 'running' ? (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                                Pending...
                              </Badge>
                            ) : run.vectorshift_raw_response_text ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                                ✓ Has Raw
                              </Badge>
                            ) : run.vectorshift_response_status ? (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                                Status Only
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                                ✗ No Data
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {run.missing_required_fields?.length > 0 && (
                              <Badge variant="destructive" className="mr-1">
                                {run.missing_required_fields.length} required
                              </Badge>
                            )}
                            {run.missing_optional_fields?.length > 0 && (
                              <Badge variant="secondary">
                                {run.missing_optional_fields.length} optional
                              </Badge>
                            )}
                            {!run.missing_required_fields?.length && !run.missing_optional_fields?.length && '-'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(run.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {(run.status === 'running' || run.status === 'pending') && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  title="Stop this run"
                                  disabled={stoppingRunId === run.id}
                                  onClick={() => handleStopRun(run.id)}
                                >
                                  {stoppingRunId === run.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <XCircle className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                title="View run details"
                                onClick={() => {
                                  setSelectedRun(run);
                                  setShowRunDetail(true);
                                  setRunDetailTab('summary');
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                title="View in Results Page"
                                onClick={() => navigate(`/resume-results-templated?runId=${run.id}`)}
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Pipeline Form Dialog */}
        <Dialog open={showPipelineForm} onOpenChange={setShowPipelineForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPipeline ? 'Edit Pipeline' : 'Add New Pipeline'}</DialogTitle>
              <DialogDescription>
                Configure a VectorShift pipeline with detailed input/output specifications
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Pipeline Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Resume Tailoring - Production"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pipeline_id">VectorShift Pipeline ID</Label>
                  <Input
                    id="pipeline_id"
                    value={formData.pipeline_id}
                    onChange={(e) => setFormData({ ...formData, pipeline_id: e.target.value })}
                    placeholder="e.g., pip_WDkbSCpKQxhPWN9B"
                    className="font-mono"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="environment">Environment</Label>
                  <Select
                    value={formData.environment}
                    onValueChange={(value: 'test' | 'production') => setFormData({ ...formData, environment: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="test">Test</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="is_active">Active</Label>
                  <div className="flex items-center gap-2 h-10">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <span className="text-sm text-muted-foreground">
                      {formData.is_active ? 'Pipeline is active' : 'Pipeline is inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Input Specifications */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Input Specifications</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addInputSpec}>
                    <Plus className="w-4 h-4 mr-1" /> Add Input
                  </Button>
                </div>
                {formData.input_specs.map((spec, index) => (
                  <div key={index} className="border rounded p-3 space-y-3 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">Input #{index + 1}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeInputSpec(index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Name</Label>
                        <Input
                          value={spec.name}
                          onChange={(e) => updateInputSpec(index, 'name', e.target.value)}
                          placeholder="Job_Description"
                          className="font-mono text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Type</Label>
                        <Select value={spec.type} onValueChange={(v) => updateInputSpec(index, 'type', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">text</SelectItem>
                            <SelectItem value="number">number</SelectItem>
                            <SelectItem value="json">json</SelectItem>
                            <SelectItem value="boolean">boolean</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Required</Label>
                        <div className="flex items-center h-10">
                          <Switch checked={spec.required} onCheckedChange={(v) => updateInputSpec(index, 'required', v)} />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={spec.description}
                        onChange={(e) => updateInputSpec(index, 'description', e.target.value)}
                        placeholder="Full job posting text..."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Example</Label>
                      <Textarea
                        value={spec.example}
                        onChange={(e) => updateInputSpec(index, 'example', e.target.value)}
                        placeholder="Example value..."
                        rows={3}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Output Specifications */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Output Specifications</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addOutputSpec}>
                    <Plus className="w-4 h-4 mr-1" /> Add Output
                  </Button>
                </div>
                {formData.output_specs.map((spec, index) => (
                  <div key={index} className={`border rounded p-3 space-y-3 ${spec.required ? 'border-red-300 bg-red-50/30' : 'bg-muted/30'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">Output #{index + 1} {spec.required && <Badge variant="destructive" className="ml-2">Required</Badge>}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeOutputSpec(index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Name</Label>
                        <Input
                          value={spec.name}
                          onChange={(e) => updateOutputSpec(index, 'name', e.target.value)}
                          placeholder="Final_Resume_Text"
                          className="font-mono text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Type</Label>
                        <Select value={spec.type} onValueChange={(v) => updateOutputSpec(index, 'type', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">text</SelectItem>
                            <SelectItem value="number">number</SelectItem>
                            <SelectItem value="json">json</SelectItem>
                            <SelectItem value="boolean">boolean</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Required</Label>
                        <div className="flex items-center h-10">
                          <Switch checked={spec.required} onCheckedChange={(v) => updateOutputSpec(index, 'required', v)} />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={spec.description}
                        onChange={(e) => updateOutputSpec(index, 'description', e.target.value)}
                        placeholder="The tailored resume text..."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Example</Label>
                      <Textarea
                        value={spec.example}
                        onChange={(e) => updateOutputSpec(index, 'example', e.target.value)}
                        placeholder="Example value..."
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Legacy outputs */}
              <div className="space-y-2">
                <Label htmlFor="required_outputs">Required Outputs (comma-separated)</Label>
                <Textarea
                  id="required_outputs"
                  value={formData.required_outputs}
                  onChange={(e) => setFormData({ ...formData, required_outputs: e.target.value })}
                  placeholder="Final_Resume_Text, Original_Match_Score, Tailored_Match_Score"
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">Fields that must be present for a successful run</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="optional_outputs">Optional Outputs (comma-separated)</Label>
                <Textarea
                  id="optional_outputs"
                  value={formData.optional_outputs}
                  onChange={(e) => setFormData({ ...formData, optional_outputs: e.target.value })}
                  placeholder="Cover_Letter, Thank_You_Letter, Executive_Bio"
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">Fields that are nice to have but not required</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this pipeline's purpose..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowPipelineForm(false); setEditingPipeline(null); }}>
                Cancel
              </Button>
              <Button onClick={handleSavePipeline} disabled={!formData.name || !formData.pipeline_id}>
                {editingPipeline ? 'Update Pipeline' : 'Create Pipeline'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Enhanced Run Detail Dialog */}
        <Dialog open={showRunDetail} onOpenChange={setShowRunDetail}>
          <DialogContent className="max-w-6xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Run Details
                {selectedRun && getStatusBadge(selectedRun.status)}
              </DialogTitle>
              <DialogDescription>
                {selectedRun && `Run ID: ${selectedRun.id}`}
              </DialogDescription>
            </DialogHeader>
            {selectedRun && (
              <Tabs value={runDetailTab} onValueChange={setRunDetailTab} className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="request">Request Sent</TabsTrigger>
                  <TabsTrigger value="raw">Raw VectorShift</TabsTrigger>
                  <TabsTrigger value="response">Processed</TabsTrigger>
                  <TabsTrigger value="debug">Debug</TabsTrigger>
                </TabsList>

                {/* Summary Tab */}
                <TabsContent value="summary" className="mt-4">
                  <ScrollArea className="h-[60vh]">
                    <div className="space-y-6 pr-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <h4 className="text-sm font-medium mb-1">Duration</h4>
                          <p className="text-lg font-mono">
                            {selectedRun.duration_ms ? `${(selectedRun.duration_ms / 1000).toFixed(2)}s` : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium mb-1">Created</h4>
                          <p className="text-sm">{new Date(selectedRun.created_at).toLocaleString()}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium mb-1">Type</h4>
                          <p>{selectedRun.is_smoke_test ? 'Smoke Test' : 'User Request'}</p>
                        </div>
                      </div>

                      {selectedRun.error_message && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <h4 className="text-sm font-medium text-red-700 mb-1">Error</h4>
                          <p className="text-sm text-red-600 whitespace-pre-wrap">{selectedRun.error_message}</p>
                        </div>
                      )}

                      {(selectedRun.missing_required_fields?.length > 0 || selectedRun.missing_optional_fields?.length > 0) && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium">Missing Fields</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedRun.missing_required_fields?.map((field) => (
                              <Badge key={field} variant="destructive">{field}</Badge>
                            ))}
                            {selectedRun.missing_optional_fields?.map((field) => (
                              <Badge key={field} variant="secondary">{field}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Request Sent Tab */}
                <TabsContent value="request" className="mt-4">
                  <ScrollArea className="h-[60vh]">
                    <div className="space-y-6 pr-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Request Data</h3>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm">Raw View</Label>
                          <Switch checked={rawViewMode} onCheckedChange={setRawViewMode} />
                        </div>
                      </div>

                      {/* What was sent to Edge Function */}
                      <div className="border rounded-lg p-4 space-y-4">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <Badge variant="outline">1</Badge>
                          What was sent TO YOUR EDGE FUNCTION
                        </h4>
                        
                        {selectedRun.request_payload?.resumeText && (
                          renderFullTextArea(
                            'Resume Text (FULL)',
                            selectedRun.request_payload.resumeText,
                            () => copyToClipboard(selectedRun.request_payload.resumeText, 'Resume text')
                          )
                        )}
                        
                        {selectedRun.request_payload?.jobDescription && (
                          renderFullTextArea(
                            'Job Description (FULL)',
                            selectedRun.request_payload.jobDescription,
                            () => copyToClipboard(selectedRun.request_payload.jobDescription, 'Job description')
                          )
                        )}

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs">Job Title</Label>
                            <p className="text-sm font-mono">{selectedRun.request_payload?.jobTitle || '(not provided)'}</p>
                          </div>
                          <div>
                            <Label className="text-xs">Company Name</Label>
                            <p className="text-sm font-mono">{selectedRun.request_payload?.companyName || '(not provided)'}</p>
                          </div>
                        </div>

                        {selectedRun.request_payload?.additionalNotes && (
                          <div>
                            <Label className="text-xs">Additional Notes</Label>
                            <p className="text-sm">{selectedRun.request_payload.additionalNotes}</p>
                          </div>
                        )}
                      </div>

                      {/* What was sent to VectorShift */}
                      <div className="border rounded-lg p-4 space-y-4 border-blue-300 bg-blue-50/30">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <Badge className="bg-blue-500">2</Badge>
                          What was sent TO VECTORSHIFT (Exact Payload)
                        </h4>
                        
                        {selectedRun.vectorshift_request ? (
                          <>
                            <div>
                              <Label className="text-xs">Pipeline ID</Label>
                              <p className="text-sm font-mono">{selectedRun.vectorshift_request.pipeline_id}</p>
                            </div>
                            
                            {selectedRun.vectorshift_request.inputs?.Job_Description && (
                              renderFullTextArea(
                                'inputs.Job_Description (FULL)',
                                selectedRun.vectorshift_request.inputs.Job_Description,
                                () => copyToClipboard(selectedRun.vectorshift_request.inputs.Job_Description, 'VectorShift Job_Description')
                              )
                            )}
                            
                            {selectedRun.vectorshift_request.inputs?.Resume && (
                              renderFullTextArea(
                                'inputs.Resume (FULL)',
                                selectedRun.vectorshift_request.inputs.Resume,
                                () => copyToClipboard(selectedRun.vectorshift_request.inputs.Resume, 'VectorShift Resume')
                              )
                            )}

                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium">Full VectorShift Request JSON</h4>
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(JSON.stringify(selectedRun.vectorshift_request, null, 2), 'VectorShift request')}>
                                  <Copy className="w-4 h-4 mr-1" /> Copy JSON
                                </Button>
                              </div>
                              <pre className="p-4 bg-muted rounded-lg text-xs overflow-auto font-mono whitespace-pre-wrap border" style={{ maxHeight: '300px' }}>
                                {JSON.stringify(selectedRun.vectorshift_request, null, 2)}
                              </pre>
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-muted-foreground">VectorShift request not stored (older run)</p>
                        )}
                      </div>
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* RAW VECTORSHIFT RESPONSE TAB - VERBATIM */}
                <TabsContent value="raw" className="mt-4">
                  <ScrollArea className="h-[60vh]">
                    <div className="space-y-6 pr-4">
                      {/* Context-aware explanation banner */}
                      {selectedRun.error_message === 'Manually stopped by admin' ? (
                        <div className="p-4 bg-orange-50 border border-orange-300 rounded-lg">
                          <h3 className="text-lg font-bold text-orange-800 mb-2">🛑 Run Was Manually Stopped</h3>
                          <p className="text-sm text-orange-700">
                            This run was <strong>manually stopped</strong> before VectorShift returned a response.
                            There is no raw VectorShift payload because the API call was never completed.
                          </p>
                        </div>
                      ) : selectedRun.status === 'running' ? (
                        <div className="p-4 bg-blue-50 border border-blue-300 rounded-lg">
                          <h3 className="text-lg font-bold text-blue-800 mb-2">⏳ Run Still In Progress</h3>
                          <p className="text-sm text-blue-700">
                            This run is still executing. Raw VectorShift data will appear here once the API call completes.
                          </p>
                        </div>
                      ) : selectedRun.vectorshift_response_status && !selectedRun.vectorshift_raw_response_text ? (
                        <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
                          <h3 className="text-lg font-bold text-amber-800 mb-2">📦 Older Run (Pre-Raw Logging)</h3>
                          <p className="text-sm text-amber-700">
                            VectorShift responded with status <strong>{selectedRun.vectorshift_response_status}</strong>, but raw payload 
                            was not persisted. This run occurred before raw logging was fully implemented.
                          </p>
                        </div>
                      ) : !selectedRun.vectorshift_raw_response_text && !selectedRun.vectorshift_response_status ? (
                        <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
                          <h3 className="text-lg font-bold text-red-800 mb-2">❌ No VectorShift Response</h3>
                          <p className="text-sm text-red-700">
                            No VectorShift response was captured. This typically means:
                          </p>
                          <ul className="text-sm text-red-700 list-disc list-inside mt-2">
                            <li>Network timeout (5 minute limit exceeded)</li>
                            <li>Connection failure before reaching VectorShift</li>
                            <li>Run was stopped before the API call completed</li>
                          </ul>
                          {selectedRun.error_message && (
                            <p className="text-sm text-red-700 mt-2">
                              <strong>Error:</strong> {selectedRun.error_message}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                          <h3 className="text-lg font-bold text-yellow-800 mb-2">⚠️ Raw VectorShift Response (VERBATIM)</h3>
                          <p className="text-sm text-yellow-700">
                            This is the <strong>exact</strong> HTTP response body returned by VectorShift, stored before any parsing or transformation.
                          </p>
                        </div>
                      )}

                      {/* HTTP Status */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="border rounded p-3">
                          <Label className="text-xs text-muted-foreground">HTTP Status Code</Label>
                          <p className="font-mono text-lg font-bold">
                            {selectedRun.vectorshift_response_status ?? '(not recorded)'}
                          </p>
                        </div>
                        <div className="border rounded p-3">
                          <Label className="text-xs text-muted-foreground">Response Headers</Label>
                          <pre className="font-mono text-xs mt-1">
                            {selectedRun.vectorshift_response_headers 
                              ? JSON.stringify(selectedRun.vectorshift_response_headers, null, 2)
                              : '(not recorded)'}
                          </pre>
                        </div>
                      </div>

                      {/* Raw Text Response */}
                      <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50/30">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-bold text-green-800">Raw Response TEXT (Verbatim HTTP Body)</h4>
                          <div className="flex gap-2">
                            <Badge variant="secondary">
                              {selectedRun.vectorshift_raw_response_text?.length ?? 0} chars
                            </Badge>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => copyToClipboard(selectedRun.vectorshift_raw_response_text || '', 'Raw response text')}
                              disabled={!selectedRun.vectorshift_raw_response_text}
                            >
                              <Copy className="w-4 h-4 mr-1" /> Copy Raw Text
                            </Button>
                          </div>
                        </div>
                        {selectedRun.vectorshift_raw_response_text ? (
                          <pre className="p-4 bg-white border rounded text-xs overflow-auto font-mono whitespace-pre-wrap" style={{ maxHeight: '400px' }}>
                            {selectedRun.vectorshift_raw_response_text}
                          </pre>
                        ) : (
                          <p className="text-muted-foreground italic">Raw response text not available (run occurred before raw logging was implemented)</p>
                        )}
                      </div>

                      {/* Raw Parsed JSON */}
                      <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50/30">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-bold text-blue-800">Raw Response JSON (Parsed from raw text, no transformations)</h4>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => copyToClipboard(JSON.stringify(selectedRun.vectorshift_raw_response_json, null, 2), 'Raw JSON')}
                            disabled={!selectedRun.vectorshift_raw_response_json}
                          >
                            <Copy className="w-4 h-4 mr-1" /> Copy Raw JSON
                          </Button>
                        </div>
                        {selectedRun.vectorshift_raw_response_json ? (
                          <>
                            <div className="mb-2 flex flex-wrap gap-1">
                              <span className="text-xs text-muted-foreground">Keys in raw response:</span>
                              {Object.keys(selectedRun.vectorshift_raw_response_json).map(key => (
                                <Badge key={key} variant="outline" className="font-mono text-xs">{key}</Badge>
                              ))}
                            </div>
                            <pre className="p-4 bg-white border rounded text-xs overflow-auto font-mono whitespace-pre-wrap" style={{ maxHeight: '500px' }}>
                              {JSON.stringify(selectedRun.vectorshift_raw_response_json, null, 2)}
                            </pre>
                          </>
                        ) : (
                          <p className="text-muted-foreground italic">Raw JSON not available (response may not be valid JSON, or run occurred before raw logging)</p>
                        )}
                      </div>
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Processed Response Tab (was "Response") */}
                <TabsContent value="response" className="mt-4">
                  <ScrollArea className="h-[60vh]">
                    <div className="space-y-6 pr-4">
                      <div className="p-4 bg-orange-50 border border-orange-300 rounded-lg">
                        <h3 className="text-lg font-bold text-orange-800 mb-2">Processed Response Payload (Post-Transformation)</h3>
                        <p className="text-sm text-orange-700">
                          This is the <strong>response_payload</strong> stored after our app's cleanup/normalization logic runs.
                          Compare with the "Raw VectorShift" tab to see what transformations occurred.
                        </p>
                      </div>

                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold">Stored response_payload</h3>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(JSON.stringify(selectedRun.response_payload, null, 2), 'Processed response')}>
                          <Copy className="w-4 h-4 mr-1" /> Copy Processed Response
                        </Button>
                      </div>

                      {selectedRun.response_payload ? (
                        <>
                          {/* Individual Output Fields */}
                          <div className="space-y-4">
                            <h4 className="font-semibold text-sm">Individual Output Fields (after cleanup)</h4>
                            {Object.entries(selectedRun.response_payload).map(([key, value]) => {
                              const stringValue = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
                              return (
                                <div key={key} className="border rounded p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-sm font-medium">{key}</span>
                                      <Badge variant="secondary" className="text-xs">
                                        {typeof value}
                                      </Badge>
                                      {typeof value === 'string' && (
                                        <Badge variant="outline" className="text-xs">
                                          {value.length} chars
                                        </Badge>
                                      )}
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(stringValue, key)}>
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  </div>
                                  <pre className="p-2 bg-muted rounded text-xs overflow-auto font-mono whitespace-pre-wrap" style={{ maxHeight: '400px' }}>
                                    {stringValue || '(empty)'}
                                  </pre>
                                </div>
                              );
                            })}
                          </div>

                          {/* Full Processed Payload */}
                          <div className="space-y-2 border-t pt-4">
                            <h4 className="font-semibold text-sm">Full Stored response_payload</h4>
                            <pre className="p-4 bg-muted rounded-lg text-xs overflow-auto font-mono whitespace-pre-wrap border" style={{ maxHeight: '500px' }}>
                              {JSON.stringify(selectedRun.response_payload, null, 2)}
                            </pre>
                          </div>
                        </>
                      ) : (
                        <p className="text-muted-foreground">No response data available</p>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Debug Info Tab */}
                <TabsContent value="debug" className="mt-4">
                  <ScrollArea className="h-[60vh]">
                    <div className="space-y-6 pr-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="border rounded p-3">
                          <Label className="text-xs text-muted-foreground">Run ID</Label>
                          <p className="font-mono text-sm break-all">{selectedRun.id}</p>
                        </div>
                        <div className="border rounded p-3">
                          <Label className="text-xs text-muted-foreground">Pipeline Record ID</Label>
                          <p className="font-mono text-sm break-all">{selectedRun.pipeline_id || 'N/A'}</p>
                        </div>
                        <div className="border rounded p-3">
                          <Label className="text-xs text-muted-foreground">User ID</Label>
                          <p className="font-mono text-sm break-all">{selectedRun.user_id}</p>
                        </div>
                        <div className="border rounded p-3">
                          <Label className="text-xs text-muted-foreground">VectorShift Pipeline ID</Label>
                          <p className="font-mono text-sm break-all">{selectedRun.vectorshift_request?.pipeline_id || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Expected vs Actual Comparison */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-4">Expected vs Actual Outputs</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-sm font-medium mb-2">Missing Required</h5>
                            {selectedRun.missing_required_fields?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {selectedRun.missing_required_fields.map(f => (
                                  <Badge key={f} variant="destructive">{f}</Badge>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-green-600">All required outputs present ✓</p>
                            )}
                          </div>
                          <div>
                            <h5 className="text-sm font-medium mb-2">Missing Optional</h5>
                            {selectedRun.missing_optional_fields?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {selectedRun.missing_optional_fields.map(f => (
                                  <Badge key={f} variant="secondary">{f}</Badge>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-green-600">All optional outputs present ✓</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actual Keys Received */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-2">Actual Keys in Response</h4>
                        {selectedRun.response_payload ? (
                          <div className="flex flex-wrap gap-1">
                            {Object.keys(selectedRun.response_payload).map(key => (
                              <Badge key={key} variant="outline" className="font-mono text-xs">{key}</Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No response</p>
                        )}
                      </div>
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRunDetail(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPipelinesManagement;
